import React from 'react'
import type { ApisPose, LayoutOverride } from '../types'
import { mergeStyles, getTailStyle, offsetTop } from './layoutUtils'
import './animations.css'

/**
 * Gera múltiplas sources de vídeo para compatibilidade cross-browser
 * Aceita string (inferência automática) OU objeto explícito { mov, webm }
 * iOS Safari suporta .mov com transparência, Android/Desktop preferem .webm
 */
function getVideoSources(
  srcOrObj: string | { mov: string; webm: string } | undefined
): Array<{ src: string; type: string }> {
  if (!srcOrObj) return []
  
  const sources: Array<{ src: string; type: string }> = []
  
  // Objeto explícito: usar caminhos fornecidos (.mov primeiro para iOS)
  if (typeof srcOrObj === 'object') {
    if (srcOrObj.mov) sources.push({ src: srcOrObj.mov, type: 'video/quicktime' })
    if (srcOrObj.webm) sources.push({ src: srcOrObj.webm, type: 'video/webm' })
    return sources
  }
  
  // String: inferir par mov/webm automaticamente
  const src = srcOrObj
  if (src.includes('_webm.webm')) {
    const movSrc = src.replace('_webm.webm', '.mov')
    sources.push({ src: movSrc, type: 'video/quicktime' })
    sources.push({ src, type: 'video/webm' })
  } else if (src.endsWith('.webm')) {
    const movSrc = src.replace(/\.webm$/, '.mov')
    sources.push({ src: movSrc, type: 'video/quicktime' })
    sources.push({ src, type: 'video/webm' })
  } else if (src.endsWith('.mov')) {
    sources.push({ src, type: 'video/quicktime' })
  } else {
    sources.push({ src, type: '' })
  }
  
  return sources
}

interface TutorialCharacterProps {
  text: string
  uiPosition?: 'default' | 'center' | 'top' | 'center-up' | 'center-down' | 'center-inverted'
  pose?: ApisPose
  videoSrc?: string | { mov: string; webm: string }  // String (inferência) OU objeto explícito
  layoutOverride?: LayoutOverride
  enableStaggeredAnimation?: boolean  // ✅ NOVO: Ativa animação pop-in escalonada
}

// Mapeamento de poses da Apis para arquivos de imagem
const APIS_IMAGES: Record<ApisPose, string> = {
  neutro: '/Imagens/Apis1.png',
  apontando: '/Imagens/Apis_Apontando.png',
  comemorando: '/Imagens/Apis_Festa.png',
  pensando: '/Imagens/Apis_Pensando.png',
  feliz: '/Imagens/Apis_Sorrindo.png',
  surpresa: '/Imagens/Apis_Surpresa.png',
  olhando_para_baixo: '/Imagens/Apis_Olhando_Para_Baixo.png',
}

const bubbleDefault: React.CSSProperties = {
  position: 'fixed',
  right: 140,
  bottom: 60,
  maxWidth: 360,
  background: 'linear-gradient(135deg, #ffffff, #f8f9fa)',
  color: '#111',
  padding: '14px 16px',
  borderRadius: 12,
  border: '2px solid #8B5CF6',
  boxShadow: '0 4px 12px rgba(0,0,0,0.08), 0 12px 32px rgba(0,0,0,0.12)',
  zIndex: 12000,
  fontSize: 14,
  lineHeight: 1.4,
  animation: 'slideInFromRight 0.3s ease-out forwards',
}

const imgDefault: React.CSSProperties = {
  position: 'fixed',
  right: 24,
  bottom: 24,
  width: 200,
  height: 'auto',
  zIndex: 12000,
  pointerEvents: 'none',
  animation: 'fadeInScale 0.4s cubic-bezier(0.34, 1.56, 0.64, 1) forwards, floatVertical 4s ease-in-out 0.4s infinite',
}

const videoDefault: React.CSSProperties = {
  position: 'fixed',
  right: 24,
  bottom: 24,
  width: 200,
  height: 'auto',
  zIndex: 12000,
  pointerEvents: 'none',
  opacity: 1,
  // ✅ iOS: forçar compositing + transparência em vídeos .mov
  WebkitMaskImage: '-webkit-radial-gradient(white, white)',
  mixBlendMode: 'normal',
  // ✅ iOS: Background transparente explícito
  background: 'transparent',
  backgroundColor: 'transparent',
}

const centerContainer: React.CSSProperties = {
  position: 'fixed',
  top: '58%',
  left: '50%',
  transform: 'translate(-50%, -50%)',
  zIndex: 12000,
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  gap: 12,
  maxWidth: 420,
}

const centerUpContainer: React.CSSProperties = {
  ...centerContainer,
  top: '45%',
}

const centerDownContainer: React.CSSProperties = {
  ...centerContainer,
  top: '60%',
}

const bubbleCenter: React.CSSProperties = {
  width: '100%',
  background: 'linear-gradient(135deg, #ffffff, #f8f9fa)',
  color: '#111',
  padding: '14px 16px',
  borderRadius: 12,
  border: '2px solid #8B5CF6',
  boxShadow: '0 4px 12px rgba(0,0,0,0.08), 0 12px 32px rgba(0,0,0,0.12)',
  fontSize: 14,
  lineHeight: 1.45,
  animation: 'slideInFromBottom 0.3s ease-out forwards',
  position: 'relative',
}

// Tail do balão (seta apontando para cima - para Apis) - position: center
const bubbleTailCenter: React.CSSProperties = {
  position: 'absolute',
  top: -8,
  left: '50%',
  transform: 'translateX(-50%)',
  width: 0,
  height: 0,
  borderBottom: '8px solid #8B5CF6',
  borderLeft: '8px solid transparent',
  borderRight: '8px solid transparent',
}

const imgCenter: React.CSSProperties = {
  width: 240,
  height: 'auto',
  pointerEvents: 'none',
  animation: 'fadeInScale 0.4s cubic-bezier(0.34, 1.56, 0.64, 1) forwards, floatVertical 4s ease-in-out 0.4s infinite',
}

const videoCenter: React.CSSProperties = {
  width: 240,
  height: 'auto',
  pointerEvents: 'none',
  opacity: 1,  // Sem animação de fade
  // ✅ iOS: forçar compositing + transparência em vídeos .mov
  WebkitMaskImage: '-webkit-radial-gradient(white, white)',
  mixBlendMode: 'normal',
  background: 'transparent',
  backgroundColor: 'transparent',
}

const topContainer: React.CSSProperties = {
  position: 'fixed',
  top: 16,
  left: '50%',
  transform: 'translateX(-50%)',
  zIndex: 12000,
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  gap: 12,
  maxWidth: 420,
  width: 'calc(100% - 32px)',
}

const bubbleTop: React.CSSProperties = {
  width: '100%',
  background: 'linear-gradient(135deg, #ffffff, #f8f9fa)',
  color: '#111',
  padding: '14px 16px',
  borderRadius: 12,
  border: '2px solid #8B5CF6',
  boxShadow: '0 4px 12px rgba(0,0,0,0.08), 0 12px 32px rgba(0,0,0,0.12)',
  fontSize: 14,
  lineHeight: 1.45,
  animation: 'slideInFromBottom 0.3s ease-out forwards',
  position: 'relative',
}

// Tail do balão (seta apontando para cima - para Apis) - position: top
const bubbleTailTop: React.CSSProperties = {
  position: 'absolute',
  top: -8,
  left: '50%',
  transform: 'translateX(-50%)',
  width: 0,
  height: 0,
  borderBottom: '8px solid #8B5CF6',
  borderLeft: '8px solid transparent',
  borderRight: '8px solid transparent',
}

const imgTop: React.CSSProperties = {
  width: 240,
  height: 'auto',
  pointerEvents: 'none',
  animation: 'fadeInScale 0.4s cubic-bezier(0.34, 1.56, 0.64, 1) forwards, floatVertical 4s ease-in-out 0.4s infinite',
}

const videoTop: React.CSSProperties = {
  width: 240,
  height: 'auto',
  pointerEvents: 'none',
  opacity: 1,
  // ✅ iOS: forçar compositing + transparência em vídeos .mov
  WebkitMaskImage: '-webkit-radial-gradient(white, white)',
  mixBlendMode: 'normal',
  background: 'transparent',
  backgroundColor: 'transparent',
}

// ✅ NOVO: Center Inverted - Apis embaixo, balão acima, botões no topo
const centerInvertedContainer: React.CSSProperties = {
  position: 'fixed',
  top: '58%',
  left: '50%',
  transform: 'translate(-50%, -50%)',
  zIndex: 12000,
  display: 'flex',
  flexDirection: 'column-reverse',  // Inverte ordem: Apis embaixo
  alignItems: 'center',
  gap: 12,
  maxWidth: 420,
}

const bubbleCenterInverted: React.CSSProperties = {
  width: '100%',
  background: 'linear-gradient(135deg, #ffffff, #f8f9fa)',
  color: '#111',
  padding: '14px 16px',
  borderRadius: 12,
  border: '2px solid #8B5CF6',
  boxShadow: '0 4px 12px rgba(0,0,0,0.08), 0 12px 32px rgba(0,0,0,0.12)',
  fontSize: 14,
  lineHeight: 1.45,
  animation: 'slideInFromBottom 0.3s ease-out forwards',
  position: 'relative',
}

// Tail do balão apontando para BAIXO (para Apis embaixo)
const bubbleTailCenterInverted: React.CSSProperties = {
  position: 'absolute',
  bottom: -8,
  left: '50%',
  transform: 'translateX(-50%)',
  width: 0,
  height: 0,
  borderTop: '8px solid #8B5CF6',
  borderLeft: '8px solid transparent',
  borderRight: '8px solid transparent',
}

const imgCenterInverted: React.CSSProperties = {
  width: 240,
  height: 'auto',
  pointerEvents: 'none',
  animation: 'fadeInScale 0.4s cubic-bezier(0.34, 1.56, 0.64, 1) forwards, floatVertical 4s ease-in-out 0.4s infinite',
}

const videoCenterInverted: React.CSSProperties = {
  width: 240,
  height: 'auto',
  pointerEvents: 'none',
  opacity: 1,
  // ✅ iOS: forçar compositing + transparência em vídeos .mov
  WebkitMaskImage: '-webkit-radial-gradient(white, white)',
  mixBlendMode: 'normal',
  background: 'transparent',
  backgroundColor: 'transparent',
}

// Container do balão default (para posicionar o tail corretamente)
const bubbleContainerDefault: React.CSSProperties = {
  position: 'fixed',
  right: 140,
  bottom: 60,
  zIndex: 12000,
}

// Tail do balão (seta apontando para a Apis) - position: default
const bubbleTailDefault: React.CSSProperties = {
  position: 'absolute',
  right: -8,
  bottom: 20,
  width: 0,
  height: 0,
  borderLeft: '8px solid #8B5CF6',
  borderTop: '8px solid transparent',
  borderBottom: '8px solid transparent',
}

const TutorialCharacter: React.FC<TutorialCharacterProps> = ({ text, uiPosition = 'default', pose = 'neutro', videoSrc, layoutOverride, enableStaggeredAnimation = false }) => {
  const imgSrc = APIS_IMAGES[pose]
  // ✅ Múltiplas sources: iOS usa .mov, Android/Desktop usa .webm
  const videoSources = getVideoSources(videoSrc)
  const isVideo = videoSources.length > 0

  // Layouts centralizados (center, center-up, center-down)
  if (uiPosition === 'center' || uiPosition === 'center-up' || uiPosition === 'center-down') {
    let containerStyle = (() => {
      if (uiPosition === 'center-up') return centerUpContainer
      if (uiPosition === 'center-down') return centerDownContainer
      return centerContainer
    })()
    
    // ✅ Aplicar ajuste vertical via 'top' (position:fixed não respeita marginTop)
    if (layoutOverride?.character?.marginTop !== undefined) {
      const adjustedTop = offsetTop(containerStyle.top, layoutOverride.character.marginTop)
      containerStyle = {
        ...containerStyle,
        top: adjustedTop,
      }
    }

    // ✅ Aplicar layoutOverride no character (imagem ou vídeo)
    const baseCharacterStyle = isVideo ? videoCenter : imgCenter
    const characterStyle = mergeStyles(baseCharacterStyle, layoutOverride?.character)

    // ✅ Classes de animação staggered
    const characterClassName = enableStaggeredAnimation 
      ? (isVideo ? 'tutorial-staggered-character-video' : 'tutorial-staggered-character')
      : undefined
    const bubbleClassName = enableStaggeredAnimation ? 'tutorial-staggered-bubble' : undefined

    // ✅ Preparar character style sem animação inline e com variável CSS (center layout)
    let finalCharacterStyle = characterStyle
    if (enableStaggeredAnimation) {
      // Extrair scale do transform ou do override direto
      const scaleValue = layoutOverride?.character?.scale || characterStyle.transform?.match(/scale\(([^)]+)\)/)?.[1] || 1
      const { animation: _animation, ...styleWithoutAnimation } = characterStyle
      finalCharacterStyle = { ...styleWithoutAnimation, '--character-scale': scaleValue } as React.CSSProperties
    }
    
    const bubbleStyle = enableStaggeredAnimation 
      ? { ...bubbleCenter, animation: undefined }
      : bubbleCenter

    return (
      <div style={containerStyle} data-tutorial-ui="1">
        {isVideo ? (
          <video
            autoPlay
            loop
            muted
            playsInline
            style={finalCharacterStyle}
            className={characterClassName}
            aria-label="Apis em movimento"
          >
            {videoSources.map((source, idx) => (
              <source key={idx} src={source.src} type={source.type} />
            ))}
          </video>
        ) : (
          <img src={imgSrc} alt="Apis" style={finalCharacterStyle} className={characterClassName} />
        )}
        <div id="tutorial-bubble" style={bubbleStyle} className={bubbleClassName} role="status" aria-live="polite" aria-atomic="true">
          {text}
          {/* Tail apontando para a Apis (acima) */}
          <div style={bubbleTailCenter} aria-hidden="true" />
        </div>
      </div>
    )
  }

  if (uiPosition === 'top') {
    // ✅ Aplicar layoutOverride no character (imagem ou vídeo)
    const baseCharacterStyle = isVideo ? videoTop : imgTop
    const characterStyle = mergeStyles(baseCharacterStyle, layoutOverride?.character)

    // ✅ Classes de animação staggered
    const characterClassName = enableStaggeredAnimation 
      ? (isVideo ? 'tutorial-staggered-character-video' : 'tutorial-staggered-character')
      : undefined
    const bubbleClassName = enableStaggeredAnimation ? 'tutorial-staggered-bubble' : undefined

    // ✅ Preparar character style sem animação inline e com variável CSS
    let finalCharacterStyle = characterStyle
    if (enableStaggeredAnimation) {
      // Extrair scale do transform ou do override direto
      const scaleValue = layoutOverride?.character?.scale || characterStyle.transform?.match(/scale\(([^)]+)\)/)?.[1] || 1
      const { animation: _animation, ...styleWithoutAnimation } = characterStyle
      finalCharacterStyle = { ...styleWithoutAnimation, '--character-scale': scaleValue } as React.CSSProperties
    }
    
    const bubbleStyle = enableStaggeredAnimation 
      ? { ...bubbleTop, animation: undefined }
      : bubbleTop

    return (
      <div style={topContainer} data-tutorial-ui="1">
        {isVideo ? (
          <video
            autoPlay
            loop
            muted
            playsInline
            style={finalCharacterStyle}
            className={characterClassName}
            aria-label="Apis em movimento"
          >
            {videoSources.map((source, idx) => (
              <source key={idx} src={source.src} type={source.type} />
            ))}
          </video>
        ) : (
          <img src={imgSrc} alt="Apis" style={finalCharacterStyle} className={characterClassName} />
        )}
        <div id="tutorial-bubble" style={bubbleStyle} className={bubbleClassName} role="status" aria-live="polite" aria-atomic="true">
          {text}
          {/* Tail apontando para a Apis (acima) */}
          <div style={bubbleTailTop} aria-hidden="true" />
        </div>
      </div>
    )
  }

  // ✅ NOVO: Layout center-inverted (Apis embaixo, balão acima, botões no topo)
  if (uiPosition === 'center-inverted') {
    let containerStyle = centerInvertedContainer
    
    // ✅ Aplicar ajuste vertical via 'top' (position:fixed não respeita marginTop)
    if (layoutOverride?.character?.marginTop !== undefined) {
      const adjustedTop = offsetTop(containerStyle.top, layoutOverride.character.marginTop)
      containerStyle = {
        ...containerStyle,
        top: adjustedTop,
      }
    }

    // ✅ Aplicar layoutOverride no character (imagem ou vídeo)
    const baseCharacterStyle = isVideo ? videoCenterInverted : imgCenterInverted
    const characterStyle = mergeStyles(baseCharacterStyle, layoutOverride?.character)

    // ✅ Classes de animação staggered
    const characterClassName = enableStaggeredAnimation 
      ? (isVideo ? 'tutorial-staggered-character-video' : 'tutorial-staggered-character')
      : undefined
    const bubbleClassName = enableStaggeredAnimation ? 'tutorial-staggered-bubble' : undefined

    // ✅ Preparar character style sem animação inline e com variável CSS
    let finalCharacterStyle = characterStyle
    if (enableStaggeredAnimation) {
      // Extrair scale do transform ou do override direto
      const scaleValue = layoutOverride?.character?.scale || characterStyle.transform?.match(/scale\(([^)]+)\)/)?.[1] || 1
      const { animation: _animation, ...styleWithoutAnimation } = characterStyle
      finalCharacterStyle = { ...styleWithoutAnimation, '--character-scale': scaleValue } as React.CSSProperties
    }
    
    const bubbleStyle = enableStaggeredAnimation 
      ? { ...bubbleCenterInverted, animation: undefined }
      : bubbleCenterInverted

    return (
      <div style={containerStyle} data-tutorial-ui="1">
        {isVideo ? (
          <video
            autoPlay
            loop
            muted
            playsInline
            style={finalCharacterStyle}
            className={characterClassName}
            aria-label="Apis em movimento"
          >
            {videoSources.map((source, idx) => (
              <source key={idx} src={source.src} type={source.type} />
            ))}
          </video>
        ) : (
          <img src={imgSrc} alt="Apis" style={finalCharacterStyle} className={characterClassName} />
        )}
        <div id="tutorial-bubble" style={bubbleStyle} className={bubbleClassName} role="status" aria-live="polite" aria-atomic="true">
          {text}
          {/* Tail apontando para BAIXO (para Apis embaixo) */}
          <div style={bubbleTailCenterInverted} aria-hidden="true" />
        </div>
      </div>
    )
  }

  // Layout padrão (default) - suporta layoutOverride
  const baseCharacterStyle = isVideo ? videoDefault : imgDefault
  const characterStyle = mergeStyles(baseCharacterStyle, layoutOverride?.character)
  const bubbleContainerStyle = mergeStyles(bubbleContainerDefault, layoutOverride?.bubble)
  const tailStyle = getTailStyle(bubbleTailDefault, layoutOverride?.tail)
  
  // ✅ Classes de animação staggered
  const characterClassName = enableStaggeredAnimation 
    ? (isVideo ? 'tutorial-staggered-character-video' : 'tutorial-staggered-character')
    : undefined
  const bubbleClassName = enableStaggeredAnimation ? 'tutorial-staggered-bubble' : undefined

  // ✅ Preparar character style sem animação inline e com variável CSS (default layout)
  let finalCharacterStyle = characterStyle
  let finalBubbleContainerStyle = bubbleContainerStyle
  if (enableStaggeredAnimation) {
    // Extrair scale do transform ou do override direto
    const scaleValue = layoutOverride?.character?.scale || characterStyle.transform?.match(/scale\(([^)]+)\)/)?.[1] || 1
    const { animation: _charAnim, ...charStyleWithoutAnimation } = characterStyle
    const { animation: _bubbleAnim, ...bubbleStyleWithoutAnimation } = bubbleContainerStyle
    finalCharacterStyle = { ...charStyleWithoutAnimation, '--character-scale': scaleValue } as React.CSSProperties
    finalBubbleContainerStyle = bubbleStyleWithoutAnimation
  }

  // Preparar bubbleDefault sem animação se staggered
  const bubbleInnerStyle = enableStaggeredAnimation 
    ? { ...bubbleDefault, animation: undefined }
    : bubbleDefault

  return (
    <>
      <div style={finalBubbleContainerStyle} data-tutorial-ui="1" className={bubbleClassName}>
        <div 
          id="tutorial-bubble" 
          style={{
            ...bubbleInnerStyle,
            position: 'relative',
            right: 'auto',
            bottom: 'auto',
            textAlign: layoutOverride?.bubble?.textAlign,
          }} 
          role="status" 
          aria-live="polite" 
          aria-atomic="true"
        >
          {text}
          {/* Tail apontando para a Apis */}
          {(!layoutOverride?.tail || layoutOverride?.tail?.placement !== 'none') && (
            <div style={tailStyle} aria-hidden="true" />
          )}
        </div>
      </div>
      {isVideo ? (
        <video
          autoPlay
          loop
          muted
          playsInline
          style={finalCharacterStyle}
          className={characterClassName}
          aria-label="Apis em movimento"
        >
          {videoSources.map((source, idx) => (
            <source key={idx} src={source.src} type={source.type} />
          ))}
        </video>
      ) : (
        <img 
          src={imgSrc} 
          alt="Apis" 
          style={finalCharacterStyle}
          className={characterClassName}
          loading="lazy"
          decoding="async"
        />
      )}
    </>
  )
}

export default TutorialCharacter



